/**
 * QueueTab — bank-backed queue for a campaign flow. Rotation controls + preview
 * on top, then a Segment (Queue / Testing / Eliminated) + Layer filter, then a
 * table whose columns are tailored to the active segment. Eliminated rows can
 * be brought back to the queue from here.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';
import type {
  CapacityRule,
  DemoteDestination,
  QueueElement,
} from '@trafficpulse/shared';

import { Button, ConfirmDialog, LoadingSpinner } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/api-error';
import { useToast } from '@/providers/ToastProvider';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { useFlowOptimizerQueue, useQueuePriority, useRotation } from '../../hooks';
import { applyDescription } from './rotation/apply-summary';
import { announceRotation, announceRotationError } from '@/features/flow-optimizer/tabs/queue/rotation/outcome/rotation-notice';
import RotationControls from './rotation/RotationControls';
import RotationPreviewPanel from './rotation/RotationPreviewPanel';
import {
  liveFlowSignature,
  useLiveFlowPreviewSync,
  useLivePreviewRefresh,
} from './rotation/use-live-preview-refresh';
import { useOptionalFlowOptimizerLivePreview } from '../../context/FlowOptimizerLivePreviewContext';
import RotationHistoryList from './history/RotationHistoryList';
import { useRotationHistory } from './history/useRotationHistory';
import { useRequeueElement } from './eliminated/useRequeueElement';
import { useDemoteElement } from './live/useDemoteElement';
import QueueEmpty from './QueueEmpty';
import QueueLayerComingSoon from './QueueLayerComingSoon';
import UnmanagedNotice from './UnmanagedNotice';
import QueueFilters, { type QueueView } from './QueueFilters';
import BulkActionBar from './bulk/BulkActionBar';
import BulkConfirmDialog from './bulk/BulkConfirmDialog';
import BulkResultPanel from './bulk/results/BulkResultPanel';
import { useBulkQueueActions } from './bulk/controller/useBulkQueueActions';
import { toWaitingActionFor } from './bulk/bulk-actions';
import { PromoteFlowProvider } from './promote/promote-flow';
import QueueSegmentTable from './table/QueueSegmentTable';
import TestingGroupedView from './table/grouped/TestingGroupedView';
import QueuedSortableView from './table/queued/QueuedSortableView';
import {
  buildRotationSchema,
  poolElementKey,
  type RotationSwap,
} from './rotation/rotation-swaps';
import { type QueueCellCtx } from './table/queue-columns';
import {
  compareQueueOrder,
  isLayerComingSoon,
  nextUpByLayer,
  segmentCounts,
  visibleRows,
  type QueueLayerFilter,
  type QueueSegment,
} from './queue-filters';
import {
  MoveDisabledButton,
  isPathDisabled,
  type QueueStatusFilter,
} from './status';

export interface QueueTabProps {
  campaignId: string;
  provider: QueueProvider;
  /** Pushes paused → "Move to live now" is blocked (the server refuses too). */
  killSwitch?: boolean;
  /** Draft caps, so the promote dialog can offer to raise one in place. */
  capacityRules?: CapacityRule[];
  onCapacityRulesChange?: (next: CapacityRule[]) => void;
}

const QueueTab: FC<QueueTabProps> = ({
  campaignId,
  provider,
  killSwitch = false,
  capacityRules = [],
  onCapacityRulesChange,
}) => {
  const toast = useToast();
  const queueQuery = useFlowOptimizerQueue(campaignId, provider);
  const priorityMutation = useQueuePriority(campaignId, provider);
  const historyQuery = useRotationHistory(campaignId, provider);
  const requeue = useRequeueElement(campaignId, provider);
  const demote = useDemoteElement(campaignId, provider);
  const { preview, apply } = useRotation(campaignId, provider);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [view, setView] = useState<QueueView>('queued');
  // Paths-focused launch: default to paths (landers/offers are "available soon").
  const [layer, setLayer] = useState<QueueLayerFilter>('paths');
  const [statusFilter, setStatusFilter] = useState<QueueStatusFilter>('all');
  const [search, setSearch] = useState('');

  // A queue change (move to queue/waiting, reorder, bring back) re-plans the
  // shown preview so its swaps never go stale mid-session.
  useLivePreviewRefresh(queueQuery.dataUpdatedAt, preview);
  // …and when the LIVE set itself changes (promote, eliminate, apply), refresh
  // the pinned weights panel too — it reads the flow, not the config draft, so
  // nothing else would tell it to.
  const livePreview = useOptionalFlowOptimizerLivePreview();
  useLiveFlowPreviewSync(
    liveFlowSignature(queueQuery.data),
    livePreview?.live.refetch,
  );

  const data = queueQuery.data;
  const segment: QueueSegment = view === 'history' ? 'queued' : view;
  // Hide gated (coming-soon) layers everywhere, incl. the "All" view. The pill
  // counts derive from the same helper, so they always match what is listed.
  // Live only: everywhere else the axis is meaningless, so it is pinned to
  // 'all' rather than leaking a stale "Disabled" narrowing onto another tab.
  const status: QueueStatusFilter = segment === 'testing' ? statusFilter : 'all';
  const rows = visibleRows(data, segment, layer, { status, search });
  /** Every live row regardless of STATUS — the source for the status counts.
   *  The search still applies: "move all disabled" acts on this list rather
   *  than on the checkbox selection, and it must never touch a row the search
   *  is hiding. */
  const liveRows = visibleRows(data, 'testing', layer);
  const disabledRows = visibleRows(data, 'testing', layer, { search }).filter(
    isPathDisabled,
  );
  /** Unfiltered count for the "Showing N of M" line — the pills stay whole. */
  const totalRows = visibleRows(data, segment, layer, { status }).length;
  // The NEXT rotation, paired out→in from the planned diffs the listing
  // already carries: at-risk chips on testing rows, "replaces X" on queued.
  // Keyed per POOL — a bank element live on several paths gets one verdict
  // per pool, not one verdict smeared across every row.
  const planned = data?.planned ?? [];
  const plannedSchema = buildRotationSchema(planned);
  const atRisk = new Map<string, RotationSwap>(
    plannedSchema.swaps.filter((s) => s.out).map((s) => [poolElementKey(s.out!), s]),
  );
  const blocked = new Map(plannedSchema.blocked.map((d) => [poolElementKey(d), d]));
  // Value carries the tracker id too ("Name · #id") so three similarly-named
  // paths (Greenluck / Greenluck BB / Greenluck Sin) are unambiguous.
  const incoming = new Map<string, string>(
    plannedSchema.swaps
      .filter((s) => s.in && s.out)
      .map((s) => [
        poolElementKey(s.in!),
        s.out!.itemId.startsWith('bank:')
          ? s.out!.itemName
          : `${s.out!.itemName} · #${s.out!.itemId}`,
      ]),
  );
  const result = apply.data ?? preview.data;
  // The dialog must describe the plan the PANEL above it is showing. The
  // listing's `planned` is computed with the queue read, so it only refreshes
  // when the queue itself changes — edit a cap or a rule and it keeps the old
  // snapshot, while "Preview rotation" re-plans server-side (and re-runs itself
  // whenever the queue moves, see use-live-preview-refresh). That gap had the
  // dialog announcing "no changes are planned" over a preview listing two
  // evictions. `apply.data` is deliberately NOT consulted here: it records what
  // already ran, not what the next apply would do.
  const applyText = applyDescription(
    preview.data?.diffs ?? planned, result?.trackerWritesEnabled, provider, result?.error,
  );

  const onSetPriority = (element: QueueElement, priority: number | null) =>
    priorityMutation.mutate(
      { layer: element.layer, bankId: element.bankId, priority },
      { onError: () => toast.error('Could not update priority') },
    );
  const DEMOTE_TOAST: Record<DemoteDestination, string> = {
    queue: 'Moved to queue',
    waiting: 'Moved to waiting',
    eliminated: 'Eliminated',
  };
  const onDemote = (
    element: QueueElement,
    to: DemoteDestination,
    coverFloor?: boolean,
  ) =>
    demote.mutate(
      { layer: element.layer, bankId: element.bankId, to, coverFloor },
      {
        // Name what went live too — a swap writes twice and the toast is the
        // only place the second write is confirmed.
        onSuccess: (res) =>
          toast.success(
            res.covered
              ? `${DEMOTE_TOAST[to]} · "${res.covered.itemName}" is live`
              : DEMOTE_TOAST[to],
          ),
        // The server explains WHY it refused (kill switch, live floor, tracker
        // error) — surfacing that beats a generic failure message.
        onError: (err) =>
          toast.error(
            (err as { response?: { data?: { error?: { message?: string } } } })
              ?.response?.data?.error?.message ??
              'Could not move the element',
          ),
      },
    );
  // Returns the promise so the row's dialog can stay busy until the write AND
  // its refetch settle — otherwise the dialog closed instantly and nothing on
  // screen changed for seconds, which reads as a dead button.
  const onRequeue = async (element: QueueElement, to: 'queue' | 'waiting') => {
    try {
      await requeue.mutateAsync({ layer: element.layer, bankId: element.bankId, to });
      toast.success(
        to === 'waiting' ? 'Brought back to waiting' : 'Brought back to queue',
      );
    } catch (e) {
      toast.error('Could not bring the element back', getApiErrorMessage(e));
      throw e;
    }
  };
  const onPreview = () =>
    preview.mutate(undefined, {
      onError: (e) => toast.error('Could not preview rotation', getApiErrorMessage(e)),
    });
  const confirmApply = () =>
    apply.mutate(undefined, {
      onSuccess: (r) => {
        announceRotation(toast, r, provider);
        setConfirmOpen(false);
      },
      // Show the tracker's own reason (e.g. Binom "At least one Offer must
      // be created."), not a generic Axios status line.
      onError: (e) => announceRotationError(toast, e, provider),
    });

  // Multi-select + one action for the whole selection. Lives here because only
  // this component knows the segment, the visible rows and the pool caps.
  const bulk = useBulkQueueActions({
    campaignId,
    provider,
    segment,
    // The queued view re-sorts into priority order, and a bulk promote spends
    // each pool's headroom top-down — so it has to plan against the order the
    // user actually sees, not the order the listing happened to arrive in.
    rows: segment === 'queued' ? [...rows].sort(compareQueueOrder) : rows,
    pools: data?.pools ?? [],
    layer,
    status,
  });
  // A batch runs one element at a time and each write plus its refetch can take
  // seconds, so every row says where it is: spinner on the one being written,
  // tick on the ones already done.
  const doneBankIds = new Set(bulk.runner.results.map((r) => r.element.bankId));
  const bulkRow = {
    isSelected: bulk.selection.isSelected,
    onToggleSelect: bulk.selection.toggle,
    selectable: !bulk.runner.running,
    // ONLY while the batch runs. `results` deliberately outlive the run so the
    // result panel can report it, and reading them afterwards left rows showing
    // a tick where their checkbox belongs — permanently, until a reload.
    statusOf: (el: QueueElement) => {
      if (!bulk.runner.running) return 'idle' as const;
      if (bulk.runner.activeBankId === el.bankId) return 'running' as const;
      return doneBankIds.has(el.bankId) ? ('done' as const) : ('idle' as const);
    },
  };

  const promoteFlow = {
    campaignId,
    provider,
    killSwitch,
    poolIds: (data?.pools ?? []).map((p) => p.poolId),
    capacityRules,
    onCapacityRulesChange,
  };

  const ctx: QueueCellCtx = {
    pools: data?.pools ?? [],
    onSetPriority,
    // Priority PATCHes are provider-agnostic bank routes — no provider guard.
    priorityDisabled: priorityMutation.isPending,
    nextUp: nextUpByLayer(data?.queued ?? []),
    incoming,
    campaignId,
    provider,
    onRequeue,
    requeuePending: requeue.isPending,
    onDemote,
    demotePending: demote.isPending,
    bulk: bulkRow,
  };

  if (queueQuery.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <LoadingSpinner />
      </div>
    );
  }

  if (queueQuery.isError) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3">
        <p className="text-xs text-destructive">Could not load the queue.</p>
        <Button variant="outline" size="sm" onClick={() => queueQuery.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <RotationControls
        onPreview={onPreview}
        previewPending={preview.isPending}
        hasPreview={Boolean(result)}
        onApplyClick={() => setConfirmOpen(true)}
        applyPending={apply.isPending}
      />
      {result && view !== 'history' ? <RotationPreviewPanel result={result} /> : null}
      <UnmanagedNotice
        pools={data?.pools ?? []}
        provider={provider}
        campaignId={campaignId}
      />
      <QueueFilters
        view={view}
        // A term typed against Live means nothing on Eliminated, and a switch
        // that silently hides most of the new segment reads as an empty tab.
        onView={(next) => {
          setSearch('');
          setView(next);
        }}
        layer={layer}
        onLayer={setLayer}
        counts={segmentCounts(data, layer)}
        historyCount={historyQuery.data?.runs.length}
        status={segment === 'testing' && view !== 'history' ? statusFilter : undefined}
        onStatus={setStatusFilter}
        statusCounts={{
          // All three come from the SAME unfiltered set. Mixing a
          // search-filtered `disabled` in here made active = all − disabled
          // drift as you typed, so the pills stopped adding up.
          all: liveRows.length,
          active: liveRows.length - liveRows.filter(isPathDisabled).length,
          disabled: liveRows.filter(isPathDisabled).length,
        }}
        search={search}
        onSearch={setSearch}
        statusAction={
          // Only when there is actually something to clear.
          !isLayerComingSoon(layer) ? (
            <MoveDisabledButton
              disabledRows={disabledRows}
              busy={bulk.runner.running}
              onMove={(rows) => {
                const action = toWaitingActionFor(segment);
                if (action) bulk.startFor(action, rows);
              }}
            />
          ) : null
        }
      />
      {view !== 'history' && !isLayerComingSoon(layer) && rows.length > 0 ? (
        <>
          <BulkActionBar
            count={bulk.selection.count}
            allSelected={bulk.selection.allSelected}
            actions={bulk.actions}
            running={bulk.runner.running}
            done={bulk.runner.done}
            total={bulk.runner.total}
            onAction={bulk.start}
            onToggleAll={bulk.selection.toggleAll}
            onClear={bulk.selection.clear}
            onCancel={bulk.runner.cancel}
          />
          <BulkResultPanel
            results={bulk.runner.results}
            onDismiss={bulk.runner.reset}
          />
        </>
      ) : null}
      {/* The segment pills deliberately keep counting the WHOLE segment (see
          queue-filters), so the narrowing has to be reported somewhere or the
          list silently disagrees with the pill above it. */}
      {view !== 'history' && search.trim() && rows.length > 0 ? (
        <p className="px-1 text-[11px] text-muted-foreground">
          Showing <span className="font-medium text-foreground">{rows.length}</span>{' '}
          of {totalRows} ·{' '}
          <button
            type="button"
            onClick={() => setSearch('')}
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            clear
          </button>
        </p>
      ) : null}
      {view === 'history' ? (
        <RotationHistoryList
          history={historyQuery.data}
          isLoading={historyQuery.isLoading}
          isError={historyQuery.isError}
          onRetry={() => historyQuery.refetch()}
          search={search}
          onClearSearch={() => setSearch('')}
        />
      ) : isLayerComingSoon(layer) ? (
        <QueueLayerComingSoon layer={layer} />
      ) : rows.length === 0 ? (
        <QueueEmpty
          segment={segment}
          layer={layer}
          search={search}
          onClearSearch={() => setSearch('')}
        />
      ) : segment === 'testing' && layer === 'all' ? (
        <TestingGroupedView
          elements={rows}
          pools={data?.pools ?? []}
          atRisk={atRisk}
          blocked={blocked}
          campaignId={campaignId}
          provider={provider}
          onDemote={onDemote}
          demotePending={demote.isPending}
          bulk={bulkRow}
        />
      ) : segment === 'waiting' ? (
        // Through the shared table so Waiting gets the sortable Added /
        // Waiting-since columns for free; the PromoteFlowProvider is still
        // needed by the row's "Move to live now" button.
        <PromoteFlowProvider {...promoteFlow}>
          <div className="space-y-1.5">
            <p className="px-1 text-[11px] text-muted-foreground">
              Added to the bank but not in the queue yet. Move an element to the
              queue so the next rotation can promote it.
            </p>
            <QueueSegmentTable segment="waiting" elements={rows} ctx={ctx} />
          </div>
        </PromoteFlowProvider>
      ) : segment === 'queued' ? (
        <PromoteFlowProvider {...promoteFlow}>
          <QueuedSortableView
            elements={rows}
            incoming={incoming}
            campaignId={campaignId}
            provider={provider}
            bulk={bulkRow}
            reorderDisabled={search.trim().length > 0}
          />
        </PromoteFlowProvider>
      ) : (
        // Eliminated needs the promote flow too: "Set to live" revives the row
        // and hands straight over to the manual promote dialog.
        <PromoteFlowProvider {...promoteFlow}>
          <QueueSegmentTable segment={segment} elements={rows} ctx={ctx} />
        </PromoteFlowProvider>
      )}
      <BulkConfirmDialog
        open={bulk.pending !== null}
        action={bulk.pending}
        selected={bulk.targets}
        plan={bulk.plan}
        writesTracker={bulk.writesTracker}
        busy={bulk.runner.running}
        onConfirm={() => void bulk.confirm()}
        onCancel={bulk.cancelDialog}
      />
      <ConfirmDialog
        open={confirmOpen}
        title="Apply rotation"
        description={applyText}
        confirmLabel="Apply rotation"
        busy={apply.isPending}
        onConfirm={confirmApply}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default QueueTab;
